import { type BigNumberLike, bn } from "@opensea/utils"
import { EM_DASH } from "./constants"
import type { Locale, NumberFormatDisplay } from "./types"

export type NumberFormatOptions = {
  display?: NumberFormatDisplay
  bounded?: boolean
  prefix?: string
  suffix?: string
  custom?: Intl.NumberFormatOptions
  compactionThreshold?: number
}

export const ONE_BIL = 1_000_000_000

export const numberFormatter =
  (locale: Locale = "en-US") =>
  (
    value: BigNumberLike | undefined | null,
    {
      display = "standard",
      suffix,
      prefix,
      bounded = true,
      custom,
      compactionThreshold,
    }: NumberFormatOptions = {},
  ) => {
    if (!isValidNumber(value)) {
      return EM_DASH
    }

    const number = bn(value).toNumber()

    const baseOptions = getNumberFormatOptions({
      display,
      value: number,
      compactionThreshold,
    })

    const numberFormatOptions = {
      ...baseOptions,
      ...custom,
    }

    const lowerBound =
      numberFormatOptions.maximumFractionDigits !== undefined && number > 0
        ? bn(1, numberFormatOptions.maximumFractionDigits).toNumber()
        : undefined

    const numberPrefix = getNumberPrefix(display, number, {
      upperBound: bounded ? ONE_BIL : undefined,
      lowerBound: bounded ? lowerBound : undefined,
    })

    const formattedNumber = new Intl.NumberFormat(
      locale,
      numberFormatOptions,
    ).format(
      getBoundedNumber(display, number, {
        upperBound: bounded ? ONE_BIL : undefined,
        lowerBound: bounded ? lowerBound : undefined,
      }),
    )

    return `${prefix ?? ""}${numberPrefix}${formattedNumber}${suffix ? ` ${suffix}` : ""}`
  }

type CustomNumberFormatOptions = Intl.NumberFormatOptions & {
  notation?: "standard" | "compact"
}

export const getNumberFormatOptions = ({
  display,
  value,
  compactionThreshold = 10_000,
}: {
  display: NumberFormatDisplay
  value: number
  compactionThreshold?: number
} & Omit<Intl.NumberFormatOptions, "notation">): CustomNumberFormatOptions => {
  const displayedNumber = getDisplayedNumber(display, value)

  const numberFormatOptions = getNumberFormatOptionsByDisplay(display)

  if (displayedNumber > ONE_BIL) {
    numberFormatOptions.notation = "compact"
    numberFormatOptions.maximumFractionDigits = undefined
    numberFormatOptions.minimumFractionDigits = undefined
    return numberFormatOptions
  }

  if (
    display !== "full" &&
    display !== "usd-full" &&
    (Math.abs(displayedNumber) >= compactionThreshold ||
      (displayedNumber && displayedNumber % 1000 === 0))
  ) {
    numberFormatOptions.notation = "compact"
    numberFormatOptions.minimumFractionDigits = 0
    numberFormatOptions.maximumFractionDigits = 1

    return numberFormatOptions
  }

  if (display === "usd-compact") {
    if (displayedNumber >= 10) {
      numberFormatOptions.maximumFractionDigits = 2
    } else if (displayedNumber > 0) {
      numberFormatOptions.maximumSignificantDigits = 4
    }
  }

  return numberFormatOptions
}

export const getNumberFormatOptionsByDisplay = (
  display: NumberFormatDisplay,
): CustomNumberFormatOptions => {
  switch (display) {
    case "percent":
      return {
        style: "percent",
        maximumFractionDigits: 1,
      }
    case "usd":
      return {
        style: "currency",
        currency: "USD",
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 2,
      }
    case "usd-compact":
    case "usd-full":
      return {
        style: "currency",
        currency: "USD",
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 14,
      }
    case "full":
      return {
        maximumFractionDigits: 14,
      }
    case "compact":
      return {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    case "quantity":
      return {
        notation: "compact",
        maximumFractionDigits: 2,
      }
    default:
      return {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }
  }
}

const getDisplayedNumber = (display: NumberFormatDisplay, value: number) => {
  return display === "percent" ? bn(value).multipliedBy(100).toNumber() : value
}

type BoundedNumberOptions = {
  upperBound?: number
  lowerBound?: number
}

export const getBoundedNumber = (
  display: NumberFormatDisplay,
  value: number,
  options: BoundedNumberOptions = {},
) => {
  const displayedNumber = getDisplayedNumber(display, bn(value).toNumber())

  if (displayedNumber === 0) {
    return 0
  }

  if (options.upperBound && displayedNumber > options.upperBound) {
    return display === "percent" ? options.upperBound / 100 : options.upperBound
  }

  if (options.lowerBound && displayedNumber < options.lowerBound) {
    return display === "percent" ? options.lowerBound / 100 : options.lowerBound
  }

  return value
}

type NumberPrefixOptions = BoundedNumberOptions

export const getNumberPrefix = (
  display: NumberFormatDisplay,
  value: number,
  options: NumberPrefixOptions = {},
) => {
  const displayedNumber = getDisplayedNumber(display, value)

  if (displayedNumber === 0) {
    return ""
  }

  if (options.upperBound && displayedNumber > options.upperBound) {
    return "> "
  }

  if (options.lowerBound) {
    return displayedNumber < options.lowerBound ? "< " : ""
  }

  return ""
}

export const isValidNumber = (
  value: BigNumberLike | null | undefined,
): value is BigNumberLike => {
  return (
    value !== undefined && value !== null && !Number.isNaN(bn(value).toNumber())
  )
}
